import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { cloneDeep, toPlainObject } from 'lodash/fp';
import { DateTime } from 'luxon';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CollectorTask, TaskStatus } from '../domain/collector-task.model';
import { Game, Video } from '../domain/game';
import { League } from '../domain/league';
import { Team } from '../domain/team';
import { GameDetailsEssentialsCardComponent } from '../game-details/game-details-essentials-card/game-details-essentials-card.component';
import { DcRosterAssignmentSummary } from '../game-details/dc-roster/dc-roster.component';
import { deepDiffObj } from '../services/deep-diff-obj';
import { GameService } from '../services/game.service';
import { GameVideoDetailsService } from '../services/game-video-details.service';
import { LeagueService } from '../services/league.service';
import { SeasonService } from '../services/season.service';
import { TeamService } from '../services/team.service';
import { TaskExchangeDialogComponent } from '../shared/task-exchange-dialog/task-exchange-dialog.component';
import { AlertService } from '../services/alert.service';
import {
  DcRosterService,
  TASKS_SOURCE_TIME_ZONE
} from '../services/dc-roster.service';

const INITIAL_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const EXPANDED_TASK_GAME_SAVE_DEBOUNCE_MS = 750;
const TASKS_DATETIME_FORMAT = 'yyyy-MM-dd HH:mm';

interface CollectorTaskRow extends CollectorTask {
  venueDatetimeDisplay: string;
  dcDatetimeDisplay: string;
  dcDatetimeTooltip: string;
  etcDatetimeDisplay: string;
  etcDatetimeTooltip: string;
}

@Component({
  selector: 'app-collector-tasks',
  templateUrl: './collector-tasks.component.html',
  styleUrls: [
    '../shared/table-row-hover.shared.css',
    './collector-tasks.component.css'
  ]
})
export class CollectorTasksComponent implements OnInit, OnDestroy {
  @ViewChild(GameDetailsEssentialsCardComponent)
  expandedTaskEssentialsComponent?: GameDetailsEssentialsCardComponent;

  readonly displayedColumns = [
    'dcType',
    'league',
    'venueDatetime',
    'dcDatetime',
    'etcDatetime',
    'teams',
    'code',
    'duration',
    'critical',
    'billingMonth',
    'status',
    'roster',
    'expand'
  ];
  readonly openTasksDisplayedColumns = [
    'dcType',
    'league',
    'venueDatetime',
    'dcDatetime',
    'etcDatetime',
    'teams',
    'code',
    'duration',
    'critical',
    'billingMonth',
    'status',
    'roster',
    'take'
  ];
  readonly detailRowColumns = ['expandedDetail'];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly statusOptions: {
    value: TaskStatus | null;
    label: string;
  }[] = [
    { value: null, label: 'Not set' },
    { value: 'yes', label: 'Yes' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'no', label: 'No' },
    { value: 'exchange', label: 'Exchange' },
    { value: 'done', label: 'Done' }
  ];

  dataSource = new MatTableDataSource<CollectorTaskRow>([]);
  loading = false;
  includeDone = false;
  dcDateFilter = '';
  pageIndex = 0;
  pageSize = INITIAL_PAGE_SIZE;
  taskCount = 0;
  expandedTaskId: string | null = null;
  seasons: number[] = [];
  leagues: League[] = [];
  expandedTaskGame: Game | null = null;
  expandedTaskGameUnmodified: Game | null = null;
  expandedTaskTeams: Team[] = [];
  expandedTaskDetailsError: string | null = null;
  expandedTaskDetailsLoading = false;
  openTasksDataSource = new MatTableDataSource<CollectorTaskRow>([]);
  openTasksLoading = false;
  openTasksPageIndex = 0;
  openTasksPageSize = INITIAL_PAGE_SIZE;
  openTasksCount = 0;

  private readonly destroy$ = new Subject<void>();
  private readonly dcDateFilterChange$ = new Subject<string>();
  private readonly expandedTaskGameChange$ = new Subject<void>();
  private expandedTaskDetailsRequestId = 0;

  private expandedTaskGameSaveInProgress = false;
  private expandedTaskGameSavePending = false;

  get expandedTaskGameSaving(): boolean {
    return this.expandedTaskGameSaveInProgress;
  }

  constructor(
    private dcRosterService: DcRosterService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private gameService: GameService,
    private gameVideoDetailsService: GameVideoDetailsService,
    private seasonService: SeasonService,
    private leagueService: LeagueService,
    private teamService: TeamService
  ) {}

  async ngOnInit(): Promise<void> {
    this.dcDateFilterChange$
      .pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((dcDate) => {
        void this.applyDcDateFilter(dcDate);
      });
    this.expandedTaskGameChange$
      .pipe(
        debounceTime(EXPANDED_TASK_GAME_SAVE_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        void this.saveExpandedTaskGame();
      });
    await this.loadLookups();
    await Promise.all([this.loadTasks(), this.loadOpenTasks()]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dcDateFilterChange$.complete();
    this.expandedTaskGameChange$.complete();
  }

  async onPageChanged(event: PageEvent): Promise<void> {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    await this.loadTasks();
  }

  async onIncludeDoneChanged(includeDone: boolean): Promise<void> {
    this.includeDone = includeDone;
    this.pageIndex = 0;
    this.expandedTaskId = null;
    this.resetExpandedTaskDetails();
    await this.loadTasks();
  }

  async onOpenTasksPageChanged(event: PageEvent): Promise<void> {
    this.openTasksPageIndex = event.pageIndex;
    this.openTasksPageSize = event.pageSize;
    await this.loadOpenTasks();
  }

  onDcDateFilterChanged(dcDate: string): void {
    this.dcDateFilterChange$.next(dcDate);
  }

  async onStatusChanged(
    task: CollectorTask,
    nextStatus: TaskStatus | null
  ): Promise<void> {
    const previousStatus = task.status ?? null;
    if (previousStatus === nextStatus) {
      return;
    }

    task.status = nextStatus;

    if (nextStatus === 'exchange') {
      await this.handleExchange(task, previousStatus);
      return;
    }

    try {
      await firstValueFrom(
        this.dcRosterService.updateTaskStatus(task._id, nextStatus)
      );
      if (nextStatus === 'no') {
        this.expandedTaskId = null;
        this.resetExpandedTaskDetails();
        await Promise.all([this.loadTasks(), this.loadOpenTasks()]);
        return;
      }
      if (!this.includeDone && nextStatus === 'done') {
        this.expandedTaskId = null;
        await this.loadTasks();
      }
    } catch (error) {
      task.status = previousStatus;
      if (error?.status === 422) {
        this.alertService.showWarning(
          this.formatTaskStatusValidationError(error, nextStatus)
        );
        return;
      }
      this.alertService.showError(
        'Could not update task status: ' + (error?.message ?? 'unknown error')
      );
    }
  }

  async handleExchange(
    task: CollectorTask,
    previousStatus: TaskStatus | null
  ): Promise<void> {
    const targetCollectorAuth0Id = await this.openExchangeDialog(task);

    if (!targetCollectorAuth0Id) {
      task.status = previousStatus;
      return;
    }
    if (targetCollectorAuth0Id === task.collectorAuthId) {
      task.status = previousStatus;
      this.alertService.showError('Please select a different collector.');
      return;
    }

    try {
      await firstValueFrom(
        this.dcRosterService.exchangeTask(task._id, targetCollectorAuth0Id)
      );
      this.expandedTaskId = null;
      this.resetExpandedTaskDetails();
      await this.loadTasks();
    } catch (error) {
      task.status = previousStatus;
      this.alertService.showError(
        'Could not exchange task: ' + (error?.message ?? 'unknown error')
      );
    }
  }

  async onTakeTask(task: CollectorTask): Promise<void> {
    try {
      await firstValueFrom(this.dcRosterService.takeOpenTask(task._id));
      this.expandedTaskId = null;
      this.resetExpandedTaskDetails();
      await Promise.all([this.loadTasks(), this.loadOpenTasks()]);
    } catch (error) {
      this.alertService.showError(
        'Could not take open task: ' + this.formatTakeTaskError(error)
      );
    }
  }

  async toggleExpanded(task: CollectorTask): Promise<void> {
    const isExpanding = this.expandedTaskId !== task._id;
    this.expandedTaskId = isExpanding ? task._id : null;

    if (!isExpanding) {
      this.resetExpandedTaskDetails();
      return;
    }

    if (this.shouldShowEssentialsTab(task) || this.shouldShowVideosTab(task)) {
      await this.loadExpandedTaskDetails(task);
    } else {
      this.resetExpandedTaskDetails();
    }
  }

  isExpanded(task: CollectorTask): boolean {
    return this.expandedTaskId === task._id;
  }

  isGmOrGoTask(task: CollectorTask): boolean {
    const code = `${task.code ?? ''}`.trim().toUpperCase();
    return code === 'GM' || code === 'GO';
  }

  isRosterReadOnly(task: CollectorTask): boolean {
    return !this.isGmOrGoTask(task);
  }

  onExpandedTaskEssentialsChange(): void {
    this.requestExpandedTaskGameSave();
  }

  shouldShowEssentialsTab(task: CollectorTask): boolean {
    return task.code === 'GO';
  }

  shouldShowVideosTab(task: CollectorTask): boolean {
    return task.code === 'GO';
  }

  isRosterIncomplete(task: CollectorTask): boolean {
    return task.rosterAssigned !== task.rosterTotal;
  }

  onRosterSummaryChanged(
    gameId: string,
    summary: DcRosterAssignmentSummary
  ): void {
    const tasks = [
      ...this.dataSource.data,
      ...this.openTasksDataSource.data
    ];

    for (const task of tasks) {
      if (task.gameId !== gameId) {
        continue;
      }
      task.rosterAssigned = summary.rosterAssigned;
      task.rosterTotal = summary.rosterTotal;
    }
  }

  async onExpandedTaskSeasonChange(_season: string): Promise<void> {
    if (!this.expandedTaskGame) {
      return;
    }
    await this.reloadExpandedTaskTeams();
    this.cleanExpandedTaskTeams();
    this.requestExpandedTaskGameSave();
  }

  async onExpandedTaskLeagueChange(_league: string): Promise<void> {
    if (!this.expandedTaskGame) {
      return;
    }
    await this.reloadExpandedTaskTeams();
    this.cleanExpandedTaskTeams();
    this.requestExpandedTaskGameSave();
  }

  onExpandedTaskIdTypeChange(_idType: string): void {
    if (!this.expandedTaskGame) {
      return;
    }
    const homeTeam = this.findExpandedTaskTeamByShortName(
      this.expandedTaskGame.homeTeam
    );
    const awayTeam = this.findExpandedTaskTeamByShortName(
      this.expandedTaskGame.awayTeam
    );

    if (this.expandedTaskGame.homeTeam) {
      this.syncExpandedTaskHomeTeam(homeTeam);
    }
    if (this.expandedTaskGame.awayTeam) {
      this.syncExpandedTaskAwayTeam(awayTeam);
    }
    this.requestExpandedTaskGameSave();
  }

  onExpandedTaskHomeTeamChange(team: Team | null): void {
    this.syncExpandedTaskHomeTeam(team);
    this.requestExpandedTaskGameSave();
  }

  onExpandedTaskAwayTeamChange(team: Team | null): void {
    this.syncExpandedTaskAwayTeam(team);
    this.requestExpandedTaskGameSave();
  }

  private syncExpandedTaskHomeTeam(team: Team | null): void {
    if (!this.expandedTaskGame) {
      return;
    }
    if (!team) {
      this.expandedTaskGame.homeTeam = null;
      this.expandedTaskGame.homeTeamId = null;
      return;
    }
    const homeTeamId =
      team[this.teamService.getFieldNameByIdType(this.expandedTaskGame.idType)];
    this.expandedTaskGame.homeTeam = team.shortName;
    this.expandedTaskGame.homeTeamId = homeTeamId;
  }

  private syncExpandedTaskAwayTeam(team: Team | null): void {
    if (!this.expandedTaskGame) {
      return;
    }
    if (!team) {
      this.expandedTaskGame.awayTeam = null;
      this.expandedTaskGame.awayTeamId = null;
      return;
    }
    const awayTeamId =
      team[this.teamService.getFieldNameByIdType(this.expandedTaskGame.idType)];
    this.expandedTaskGame.awayTeam = team.shortName;
    this.expandedTaskGame.awayTeamId = awayTeamId;
  }

  onExpandedTaskVideoDetailsChange(): void {
    if (this.isVideosPayloadValid(this.expandedTaskGame?.videos)) {
      this.requestExpandedTaskGameSave();
    }
  }

  onExpandedTaskAddVideo(index: number): void {
    if (!this.expandedTaskGame) {
      return;
    }
    this.gameVideoDetailsService.addVideo(this.expandedTaskGame, index);
    if (this.isVideosPayloadValid(this.expandedTaskGame.videos)) {
      this.requestExpandedTaskGameSave();
    }
  }

  onExpandedTaskRemoveVideo(video: Video): void {
    if (!this.expandedTaskGame) {
      return;
    }
    this.gameVideoDetailsService.removeVideo(this.expandedTaskGame, video);
    this.requestExpandedTaskGameSave();
  }

  onExpandedTaskVideoUrlChange(video: Video): void {
    this.gameVideoDetailsService.updateVideoFormatFromUrl(video);
    this.requestExpandedTaskGameSave();
  }

  onExpandedTaskLiveStreamAdded(video: Video): void {
    if (!this.expandedTaskGame) {
      return;
    }
    this.gameVideoDetailsService.addLiveStream(this.expandedTaskGame, video);
    this.requestExpandedTaskGameSave();
  }

  async onExpandedTaskUploadVideo(video?: Video): Promise<void> {
    if (!this.expandedTaskGame) {
      return;
    }
    const uploaded = await this.gameVideoDetailsService.uploadVideo(
      this.expandedTaskGame,
      this.leagues,
      video
    );
    if (uploaded) {
      this.requestExpandedTaskGameSave();
    }
  }

  async onExpandedTaskDownloadVideo(video: Video): Promise<void> {
    if (!this.expandedTaskGame) {
      return;
    }
    await this.gameVideoDetailsService.downloadVideo(
      this.expandedTaskGame,
      video
    );
  }

  private async openExchangeDialog(
    task: CollectorTask
  ): Promise<string | null> {
    const dialogRef = this.dialog.open(TaskExchangeDialogComponent, {
      disableClose: true,
      width: '520px',
      data: {
        currentCollectorAuthId: task.collectorAuthId,
        eligibilityContext: {
          gameId: task.gameId,
          eventCode: task.code,
          eventId: task._id,
          dcTypeOverride: task.dcTypeOverride ?? ''
        }
      }
    });
    const selectedCollectorAuth0Id = await firstValueFrom(
      dialogRef.afterClosed()
    );

    if (!selectedCollectorAuth0Id) {
      return null;
    }

    const normalizedCollectorAuth0Id = selectedCollectorAuth0Id.trim();
    return normalizedCollectorAuth0Id || null;
  }

  private async loadTasks(): Promise<void> {
    try {
      this.loading = true;
      const response = await firstValueFrom(
        this.dcRosterService.getTasks(
          this.pageIndex,
          this.pageSize,
          this.includeDone,
          this.dcDateFilter
        )
      );
      this.taskCount = response?.count ?? 0;
      this.dataSource.data = (response?.data ?? []).map((task) =>
        this.toCollectorTaskRow(task)
      );
      const expandedTaskExists = this.dataSource.data.some(
        (task) => task._id === this.expandedTaskId
      );
      if (!expandedTaskExists) {
        this.expandedTaskId = null;
        this.resetExpandedTaskDetails();
      }
    } catch (error) {
      this.taskCount = 0;
      this.dataSource.data = [];
      this.expandedTaskId = null;
      this.resetExpandedTaskDetails();
      this.alertService.showError(
        'Could not load collector tasks: ' + (error?.message ?? 'unknown error')
      );
    } finally {
      this.loading = false;
    }
  }

  private async loadOpenTasks(): Promise<void> {
    try {
      this.openTasksLoading = true;
      const response = await firstValueFrom(
        this.dcRosterService.getOpenTasks(
          this.openTasksPageIndex,
          this.openTasksPageSize,
          this.dcDateFilter
        )
      );
      this.openTasksCount = response?.count ?? 0;
      this.openTasksDataSource.data = (response?.data ?? []).map((task) =>
        this.toCollectorTaskRow(task)
      );
    } catch (error) {
      this.openTasksCount = 0;
      this.openTasksDataSource.data = [];
      this.alertService.showError(
        'Could not load open tasks: ' + (error?.message ?? 'unknown error')
      );
    } finally {
      this.openTasksLoading = false;
    }
  }

  private async applyDcDateFilter(dcDateInput: string): Promise<void> {
    const normalizedDcDate = (dcDateInput ?? '').trim();
    if (normalizedDcDate === this.dcDateFilter) {
      return;
    }

    this.dcDateFilter = normalizedDcDate;
    this.pageIndex = 0;
    this.openTasksPageIndex = 0;
    this.expandedTaskId = null;
    this.resetExpandedTaskDetails();
    await Promise.all([this.loadTasks(), this.loadOpenTasks()]);
  }

  private async loadLookups(): Promise<void> {
    try {
      const [seasons, leagues] = await Promise.all([
        firstValueFrom(this.seasonService.getSeasons()),
        firstValueFrom(this.leagueService.getLeagues())
      ]);
      this.seasons = seasons ?? [];
      this.leagues = leagues ?? [];
    } catch (error) {
      this.seasons = [];
      this.leagues = [];
      this.alertService.showError(
        this.formatError(error, 'Could not load game detail lookups.')
      );
    }
  }

  private async loadExpandedTaskDetails(task: CollectorTask): Promise<void> {
    this.resetExpandedTaskDetails();
    const requestId = this.expandedTaskDetailsRequestId;
    this.expandedTaskDetailsLoading = true;

    try {
      const game = await firstValueFrom(this.gameService.getGame(task.gameId));
      if (requestId !== this.expandedTaskDetailsRequestId) {
        return;
      }
      this.expandedTaskGame = game;
      this.gameVideoDetailsService.ensureVideos(game);
      this.expandedTaskGameUnmodified = cloneDeep(game);
      this.expandedTaskTeams = await this.loadExpandedTaskTeams(game);
      if (requestId !== this.expandedTaskDetailsRequestId) {
        return;
      }
    } catch (error) {
      if (requestId !== this.expandedTaskDetailsRequestId) {
        return;
      }
      this.expandedTaskDetailsError = this.formatError(
        error,
        'Could not load game details.'
      );
    } finally {
      if (requestId === this.expandedTaskDetailsRequestId) {
        this.expandedTaskDetailsLoading = false;
      }
    }
  }

  private async loadExpandedTaskTeams(game: Game): Promise<Team[]> {
    if (!game?.league || !game?.season || !game?.idType) {
      return [];
    }

    const teams = await this.teamService.getTeams(
      game.league,
      game.season,
      game.idType
    );
    teams.sort(
      (a, b) =>
        (a.fullName ?? '').localeCompare(b.fullName ?? '') ||
        (a.shortName ?? '').localeCompare(b.shortName ?? '')
    );
    return teams;
  }

  private async reloadExpandedTaskTeams(): Promise<void> {
    if (!this.expandedTaskGame) {
      return;
    }
    try {
      this.expandedTaskTeams = await this.loadExpandedTaskTeams(
        this.expandedTaskGame
      );
    } catch (error) {
      this.expandedTaskTeams = [];
      this.alertService.showError(
        'Could not load teams: ' + (error?.message ?? 'unknown error')
      );
    }
  }

  private cleanExpandedTaskTeams(): void {
    if (!this.expandedTaskGame) {
      return;
    }
    delete this.expandedTaskGame.homeTeamId;
    delete this.expandedTaskGame.homeTeam;
    delete this.expandedTaskGame.awayTeamId;
    delete this.expandedTaskGame.awayTeam;
  }

  private findExpandedTaskTeamByShortName(teamShortName: string): Team | null {
    return (
      this.expandedTaskTeams.find((team) => team.shortName === teamShortName) ??
      null
    );
  }

  private requestExpandedTaskGameSave(): void {
    if (
      !this.expandedTaskGame?._id ||
      !this.expandedTaskGameUnmodified ||
      this.expandedTaskDetailsLoading ||
      !!this.expandedTaskDetailsError
    ) {
      return;
    }
    this.expandedTaskGameChange$.next();
  }

  private async saveExpandedTaskGame(): Promise<void> {
    if (this.expandedTaskGameSaveInProgress) {
      this.expandedTaskGameSavePending = true;
      return;
    }

    const game = this.expandedTaskGame;
    const gameUnmodified = this.expandedTaskGameUnmodified;
    if (!game?._id || !gameUnmodified) {
      return;
    }

    this.expandedTaskGameSaveInProgress = true;
    try {
      if (this.expandedTaskEssentialsComponent?.game?._id === game._id) {
        // Validation failures should stop this save cycle without re-queuing
        // a stale pending save. New edits during the async update can still
        // mark the save as pending after this point.
        this.expandedTaskGameSavePending = false;
        try {
          this.expandedTaskEssentialsComponent.validateAndSyncVenueData();
        } catch (_validationError) {
          console.error('Venue data validation failed', _validationError);
          return;
        }
      }

      const changes = deepDiffObj(
        toPlainObject(gameUnmodified),
        toPlainObject(game)
      );
      if (Object.keys(changes).length === 0) {
        return;
      }

      await firstValueFrom(this.gameService.update(game._id, changes));
      if (this.expandedTaskGame?._id === game._id) {
        this.expandedTaskGameUnmodified = cloneDeep(game);
      }
    } catch (error) {
      this.alertService.showError(
        'Could not save game details: ' + (error?.message ?? 'unknown error')
      );
    } finally {
      this.expandedTaskGameSaveInProgress = false;
      if (this.expandedTaskGameSavePending) {
        this.expandedTaskGameSavePending = false;
        this.requestExpandedTaskGameSave();
      }
    }
  }

  private resetExpandedTaskDetails(): void {
    this.expandedTaskDetailsRequestId += 1;
    this.expandedTaskGame = null;
    this.expandedTaskGameUnmodified = null;
    this.expandedTaskTeams = [];
    this.expandedTaskDetailsError = null;
    this.expandedTaskDetailsLoading = false;
    this.expandedTaskGameSaveInProgress = false;
    this.expandedTaskGameSavePending = false;
  }

  private formatError(error: any, fallback: string): string {
    return error?.message ?? fallback;
  }

  private formatTakeTaskError(error: any): string {
    const status = error?.status;
    if (status === 409) {
      return 'already taken by another collector.';
    }
    if (status === 403) {
      return 'you are no longer eligible for this task.';
    }
    if (status === 404) {
      return 'task not found.';
    }
    return error?.message ?? 'unknown error';
  }

  private formatTaskStatusValidationError(
    error: any,
    nextStatus: TaskStatus | null
  ): string {
    const backendMessage = error?.error?.message?.trim();
    if (backendMessage) {
      return backendMessage;
    }
    if (nextStatus === 'no') {
      return 'This task cannot be declined because your collector profile has no team assigned.';
    }
    return 'Task status update was rejected.';
  }

  private isVideosPayloadValid(videos: Video[] | undefined) {
    if (!videos) {
      return false;
    }
    return videos.every((video) => !!video.cameraAngle);
  }

  private toCollectorTaskRow(task: CollectorTask): CollectorTaskRow {
    return {
      ...task,
      venueDatetimeDisplay: this.formatVenueDateTime(
        task.venueDateTime,
        task.venueTimezoneId
      ),
      dcDatetimeDisplay: this.formatCollectorDateTime(task.dcDate, task.dcTime),
      dcDatetimeTooltip: this.originalCollectorDateTime(
        task.dcDate,
        task.dcTime
      ),
      etcDatetimeDisplay: this.formatCollectorDateTime(
        task.etcDate,
        task.etcTime
      ),
      etcDatetimeTooltip: this.originalCollectorDateTime(
        task.etcDate,
        task.etcTime
      )
    };
  }

  private formatVenueDateTime(
    venueDateTime: string | null | undefined,
    venueTimezoneId: string | null | undefined
  ): string {
    if (!venueDateTime || !venueTimezoneId) {
      return '—';
    }

    const instant = DateTime.fromISO(venueDateTime, { setZone: true });
    if (!instant.isValid) {
      return '—';
    }

    const venueTime = instant.setZone(venueTimezoneId);
    return venueTime.isValid ? venueTime.toFormat(TASKS_DATETIME_FORMAT) : '—';
  }

  private formatCollectorDateTime(
    date: string | null | undefined,
    time: string | null | undefined
  ): string {
    if (!date) {
      return '—';
    }
    if (!time) {
      return date;
    }

    const sourceDateTime = this.toSourceDateTime(date, time);
    if (!sourceDateTime) {
      return `${date} ${time}`;
    }

    return sourceDateTime.toLocal().toFormat(TASKS_DATETIME_FORMAT);
  }

  private originalCollectorDateTime(
    date: string | null | undefined,
    time: string | null | undefined
  ): string {
    if (!date) {
      return '—';
    }
    if (!time) {
      return `${date} (${TASKS_SOURCE_TIME_ZONE})`;
    }
    const sourceDateTime = this.toSourceDateTime(date, time);
    if (!sourceDateTime) {
      return `${date} ${time} (${TASKS_SOURCE_TIME_ZONE})`;
    }
    return `${sourceDateTime.toFormat(
      TASKS_DATETIME_FORMAT
    )} (${TASKS_SOURCE_TIME_ZONE})`;
  }

  private toSourceDateTime(date: string, time: string): DateTime | null {
    const sourceDateTime = DateTime.fromISO(`${date}T${time}`, {
      zone: TASKS_SOURCE_TIME_ZONE
    });
    return sourceDateTime.isValid ? sourceDateTime : null;
  }
}
